import { AlertCircle, Plus, RefreshCw, Tags, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { EmptyState } from '@/components/empty-state'
import { LoadingButton } from '@/components/loading-button'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { readableTextColor } from '@/lib/contrast'
import { trpc } from '@/trpc/client'

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/

export function CategoriesPage() {
	const utils = trpc.useUtils()
	const categoriesQuery = trpc.categories.list.useQuery()
	const [createOpen, setCreateOpen] = useState(false)

	return (
		<div className="space-y-8">
			<PageHeader
				title="Categories"
				description="Tag your items so you can filter them later. Colors are up to you."
				actions={
					<Dialog open={createOpen} onOpenChange={setCreateOpen}>
						<DialogTrigger asChild>
							<Button>
								<Plus className="size-4" />
								New category
							</Button>
						</DialogTrigger>
						<CreateCategoryDialog
							onClose={() => setCreateOpen(false)}
							onCreated={() => {
								void utils.categories.list.invalidate()
								setCreateOpen(false)
							}}
						/>
					</Dialog>
				}
			/>

			{categoriesQuery.isPending ? (
				<div className="space-y-3">
					{Array.from({ length: 4 }).map((_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: skeleton
						<Skeleton key={i} className="h-16 w-full" />
					))}
				</div>
			) : categoriesQuery.isError ? (
				<Alert variant="destructive">
					<AlertCircle />
					<AlertTitle>Couldn't load categories</AlertTitle>
					<AlertDescription>
						{categoriesQuery.error.message}
						<div className="mt-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => categoriesQuery.refetch()}
							>
								<RefreshCw className="size-4" />
								Retry
							</Button>
						</div>
					</AlertDescription>
				</Alert>
			) : categoriesQuery.data.length === 0 ? (
				<EmptyState
					icon={Tags}
					title="No categories"
					description="Add a category to start organizing your items."
				/>
			) : (
				<div className="space-y-3">
					{categoriesQuery.data.map((cat) => (
						<CategoryRow key={cat.id} category={cat} />
					))}
				</div>
			)}
		</div>
	)
}

interface Category {
	id: string
	name: string
	color: string
}

function CategoryRow({ category }: { category: Category }) {
	const utils = trpc.useUtils()
	const [editing, setEditing] = useState(false)
	const [name, setName] = useState(category.name)
	const [color, setColor] = useState(category.color)
	const [confirmOpen, setConfirmOpen] = useState(false)

	const update = trpc.categories.update.useMutation()
	const remove = trpc.categories.delete.useMutation()

	const isDirty = name !== category.name || color !== category.color
	const isValid = name.trim().length > 0 && HEX_REGEX.test(color)

	function reset() {
		setName(category.name)
		setColor(category.color)
		setEditing(false)
	}

	async function save() {
		try {
			await update.mutateAsync({ id: category.id, name: name.trim(), color })
			await utils.categories.list.invalidate()
			toast.success('Category updated')
			setEditing(false)
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not update')
		}
	}

	async function handleDelete() {
		try {
			await remove.mutateAsync({ id: category.id })
			await utils.categories.list.invalidate()
			await utils.items.list.invalidate()
			toast.success('Category deleted')
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not delete')
		}
	}

	return (
		<Card className="gap-0 py-0">
			<CardContent className="p-4">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
					<div className="flex flex-1 items-center gap-3">
						<label className="relative flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-md ring-offset-2 hover:ring-2 hover:ring-ring/30">
							<span
								className="absolute inset-0 rounded-md"
								style={{ backgroundColor: color }}
								aria-hidden
							/>
							<input
								type="color"
								className="sr-only"
								value={color}
								onChange={(e) => {
									setColor(e.target.value)
									setEditing(true)
								}}
								aria-label={`Color for ${category.name}`}
							/>
						</label>
						<div className="flex-1">
							{editing ? (
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									autoFocus
								/>
							) : (
								<button
									type="button"
									onClick={() => setEditing(true)}
									className="block w-full text-left"
								>
									<span
										className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium"
										style={{
											backgroundColor: category.color,
											color: readableTextColor(category.color),
										}}
									>
										{category.name}
									</span>
								</button>
							)}
						</div>
					</div>
					<div className="flex items-center gap-2">
						{editing ? (
							<>
								<Button
									variant="ghost"
									size="sm"
									onClick={reset}
									disabled={update.isPending}
								>
									<X className="size-4" />
									Cancel
								</Button>
								<LoadingButton
									size="sm"
									onClick={save}
									pending={update.isPending}
									disabled={!isDirty || !isValid}
								>
									Save
								</LoadingButton>
							</>
						) : (
							<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => setConfirmOpen(true)}
									aria-label={`Delete ${category.name}`}
									className="text-muted-foreground hover:text-destructive"
								>
									<Trash2 className="size-4" />
								</Button>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>
											Delete the “{category.name}” category?
										</AlertDialogTitle>
										<AlertDialogDescription>
											Items keep existing — they'll just no longer be tagged
											with this category.
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel disabled={remove.isPending}>
											Cancel
										</AlertDialogCancel>
										<LoadingButton
											variant="destructive"
											onClick={handleDelete}
											pending={remove.isPending}
										>
											<Trash2 className="size-4" />
											Delete
										</LoadingButton>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

interface CreateCategoryDialogProps {
	onClose: () => void
	onCreated: () => void
}

function CreateCategoryDialog({
	onClose,
	onCreated,
}: CreateCategoryDialogProps) {
	const [name, setName] = useState('')
	const [color, setColor] = useState('#3b82f6')
	const [error, setError] = useState<string | null>(null)
	const create = trpc.categories.create.useMutation()

	const isValid = name.trim().length > 0 && HEX_REGEX.test(color)

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		setError(null)
		try {
			await create.mutateAsync({ name: name.trim(), color })
			onCreated()
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not create category')
		}
	}

	return (
		<DialogContent>
			<form onSubmit={submit} className="space-y-4" noValidate>
				<DialogHeader>
					<DialogTitle>New category</DialogTitle>
					<DialogDescription>
						Pick a name and a color. You can change either later.
					</DialogDescription>
				</DialogHeader>

				{error ? (
					<Alert variant="destructive">
						<AlertCircle />
						<AlertTitle>Couldn't create category</AlertTitle>
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				) : null}

				<div className="space-y-3">
					<div>
						<Label htmlFor="cat-name">Name</Label>
						<Input
							id="cat-name"
							className="mt-1.5"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Garden, Office, Photography…"
							maxLength={40}
							required
						/>
					</div>
					<div>
						<Label htmlFor="cat-color">Color</Label>
						<div className="mt-1.5 flex items-center gap-3">
							<input
								id="cat-color"
								type="color"
								value={color}
								onChange={(e) => setColor(e.target.value)}
								className="h-10 w-16 cursor-pointer rounded-md border bg-card p-1"
							/>
							<Input
								value={color}
								onChange={(e) => setColor(e.target.value)}
								className="font-mono uppercase"
								maxLength={7}
							/>
							<span
								className="rounded-md px-3 py-1.5 text-xs font-medium"
								style={{
									backgroundColor: HEX_REGEX.test(color) ? color : '#888',
									color: readableTextColor(
										HEX_REGEX.test(color) ? color : '#888888',
									),
								}}
							>
								Preview
							</span>
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={onClose}
						disabled={create.isPending}
					>
						Cancel
					</Button>
					<LoadingButton
						type="submit"
						pending={create.isPending}
						disabled={!isValid}
					>
						Create
					</LoadingButton>
				</DialogFooter>
			</form>
		</DialogContent>
	)
}
